// useReleaseShares (private share bar) — mint / list / revoke a release's private `/r/{token}`
// links. The token is returned ONCE on mint (persisted server-side only as its sha256); the list is metadata
// only (never the token). Revoke tombstones (revoked_on) and stops the public resolution immediately (the
// public /r/{token} page is). Built on useBandQuery/useBandMutation → keys ['band', bandId, …].
// Publish actions are NEVER optimistic — mint/revoke are server-confirm then repaint.
import { useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError, errorMessage } from "./apiError.js";
import { pushToast } from "../toasts.js";

type ReleaseSharesRead = components["schemas"]["ReleaseSharesRead"];
type ReleaseShareDto = components["schemas"]["ReleaseShareDto"];
type ReleaseShareMintResult = components["schemas"]["ReleaseShareMintResult"];

export type { ReleaseSharesRead, ReleaseShareDto, ReleaseShareMintResult };

/** The shares list key: `['band', bandId, 'release', releaseId, 'shares']`. */
export const releaseSharesKey = (bandId: string, releaseId: string) =>
  ["band", bandId, "release", releaseId, "shares"] as const;

/**
 * `GET /bands/{bandId}/releases/{releaseId}/shares` — the share bar's revoke list: metadata only
 * ({shareId, createdOn, revokedOn, hasPassword}), NEVER the token. Keyed under the release. The active link is
 * the newest non-revoked row.
 */
export function useReleaseShares(releaseId: string | undefined) {
  const { api } = useApp();
  return useBandQuery<ReleaseSharesRead>(
    ["release", releaseId ?? "none", "shares"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/releases/{releaseId}/shares", {
        params: { path: { bandId, releaseId: releaseId as string } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { enabled: releaseId != null && releaseId !== "", staleTime: 30_000 },
  );
}

/**
 * `POST /bands/{bandId}/releases/{releaseId}/shares` — mint a private link → the opaque token, ONCE
 * (`{shareId, token, path, hasPassword, createdOn}`). Optional password (argon2id-hashed server-side). Returns
 * the mint result so the bar can show + copy the full URL immediately; invalidates the list so the revoke row
 * appears. Server-confirm then repaint (never a phantom success).
 */
export function useMintShare(releaseId: string) {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<ReleaseShareMintResult, { password?: string | null }>(
    async (band, body) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/releases/{releaseId}/shares", {
        params: { path: { bandId: band, releaseId } },
        body: { password: body.password ?? null },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: () => void qc.invalidateQueries({ queryKey: releaseSharesKey(bandId, releaseId) }),
      onError: (err) => pushToast(errorMessage(err, "Couldn't create the link")),
    },
  );
}

/**
 * `DELETE /bands/{bandId}/releases/{releaseId}/shares/{shareId}` — revoke a link → the updated list.
 * Tombstones via revoked_on; the public page stops resolving immediately. Idempotent. Invalidates the list.
 */
export function useRevokeShare(releaseId: string) {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<ReleaseSharesRead, string>(
    async (band, shareId) => {
      const { data, error, response } = await api.DELETE(
        "/api/v1/bands/{bandId}/releases/{releaseId}/shares/{shareId}",
        { params: { path: { bandId: band, releaseId, shareId } } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: () => void qc.invalidateQueries({ queryKey: releaseSharesKey(bandId, releaseId) }),
      onError: (err) => pushToast(errorMessage(err, "Couldn't revoke the link")),
    },
  );
}

/** The active (newest non-revoked) share of a list, or undefined when none is live. */
export function activeShare(read: ReleaseSharesRead | undefined): ReleaseShareDto | undefined {
  const live = (read?.items ?? []).filter((s) => !s.revokedOn);
  if (live.length === 0) return undefined;
  return live.reduce((newest, s) => ((s.createdOn ?? "") > (newest.createdOn ?? "") ? s : newest));
}
